import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { auth } from './firebase';
import { messages } from './i18n.messages';

export type Locale = 'fr' | 'en';
const LS_KEY = 'nm_locale';

interface LocaleCtx {
  locale: Locale;
  setLocale: (l: Locale) => Promise<void>;
  t: (key: keyof typeof messages, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<LocaleCtx | undefined>(undefined);

function detectInitial(): Locale {
  if (typeof window === 'undefined') return 'fr';
  const saved = window.localStorage.getItem(LS_KEY);
  if (saved === 'fr' || saved === 'en') return saved;
  return navigator.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitial);

  useEffect(() => {
    document.documentElement.lang = locale;
    window.localStorage.setItem(LS_KEY, locale);
  }, [locale]);

  const setLocale = async (l: Locale) => {
    setLocaleState(l);
    // Persist on profile so it follows the user across devices
    const u = auth.currentUser;
    if (u) {
      try {
        await updateDoc(doc(db, 'users', u.uid), { locale: l, updatedAt: serverTimestamp() });
      } catch {
        // non-fatal: localStorage already has it
      }
    }
  };

  const t = (key: keyof typeof messages, vars?: Record<string, string | number>) => {
    const entry = messages[key];
    let s = (entry?.[locale] ?? entry?.fr ?? String(key)) as string;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v));
    }
    return s;
  };

  return <Ctx.Provider value={{ locale, setLocale, t }}>{children}</Ctx.Provider>;
}

export function useT(): LocaleCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useT must be used within LocaleProvider');
  return v;
}
